import { ICE_BOT_LEVELS, type IceBotLevel } from '@halvard/ai';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useIceBotDriver } from '../bot/useIceBotDriver';
import { IceLocalGame } from '../game/ice/IceLocalGame';
import { createIceGameStore, type IcePlayer, type IcePlayers } from '../game/ice/store';
import { parseSeed, randomSeed } from '../game/mountain';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { SideDot } from '../components/SideDot';
import { useT } from '../i18n';
import './PlayBot.css';

function parsePlayer(text: string | null): IcePlayer | null {
  if (text === 'human') return 'human';
  return ICE_BOT_LEVELS.find((l) => l.id === text)?.id ?? null;
}

/** Players from `?brand=human&rime=hard`; at least one side must be a bot. */
export function icePlayersFromParams(params: URLSearchParams): IcePlayers | null {
  const brand = parsePlayer(params.get('brand'));
  const rime = parsePlayer(params.get('rime'));
  if (!brand || !rime || (brand === 'human' && rime === 'human')) return null;
  return { brand, rime };
}

/**
 * `/play/bot?brand=human&rime=hard&lake=482193`: play the ice game against a bot, or watch two
 * bots. Without players, it shows the setup.
 */
export function PlayIceBot() {
  const [params, setParams] = useSearchParams();
  const players = icePlayersFromParams(params);
  const seed = parseSeed(params.get('lake'));
  const watching = players?.brand !== 'human' && players?.rime !== 'human';
  const t = useT();
  useDocumentTitle(players && watching ? t.titles.botVsBot : t.titles.playBot);

  if (!players) {
    return (
      <IceBotSetup
        onStart={(p) =>
          setParams({ brand: p.brand, rime: p.rime, lake: String(seed ?? randomSeed()) })
        }
      />
    );
  }
  const lake = seed ?? randomSeed();
  return (
    <IceBotGame
      key={`${lake}|${players.brand}|${players.rime}`}
      seed={lake}
      players={players}
      onNewLake={() =>
        setParams({ brand: players.brand, rime: players.rime, lake: String(randomSeed()) })
      }
    />
  );
}

function IceBotGame(props: { seed: number; players: IcePlayers; onNewLake: () => void }) {
  const [store] = useState(() => createIceGameStore(props.seed, randomSeed(), props.players));
  const [botSeed] = useState(randomSeed);
  useIceBotDriver(store, botSeed);
  const t = useT();
  return (
    <IceLocalGame
      store={store}
      onNewLake={props.onNewLake}
      onReplay={() => store.getState().start(props.seed, randomSeed())}
      panelExtra={
        <div className="game-section">
          <Link to="/play/bot">{t.game.changeBots}</Link>
        </div>
      }
    />
  );
}

type Mode = 'brand' | 'rime' | 'watch';

const MODES: readonly Mode[] = ['brand', 'rime', 'watch'];

function IceBotSetup({ onStart }: { onStart: (players: IcePlayers) => void }) {
  const [mode, setMode] = useState<Mode>('brand');
  const [brandLevel, setBrandLevel] = useState<IceBotLevel>('normal');
  const [rimeLevel, setRimeLevel] = useState<IceBotLevel>('normal');
  const t = useT();
  const b = t.botSetup;
  const modeText = {
    brand: { title: b.brandTitle, detail: b.brandDetail },
    rime: { title: b.rimeTitle, detail: b.rimeDetail },
    watch: { title: b.watchTitle, detail: b.watchDetail },
  };
  const players: IcePlayers =
    mode === 'brand'
      ? { brand: 'human', rime: rimeLevel }
      : mode === 'rime'
        ? { brand: brandLevel, rime: 'human' }
        : { brand: brandLevel, rime: rimeLevel };

  return (
    <article className="page-page bot-setup">
      <h1>{b.title}</h1>
      <fieldset className="bot-picker">
        <legend>{b.you}</legend>
        <div className="bot-strategies">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className="bot-strategy"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              <strong>
                {m !== 'watch' && <SideDot side={m} />}
                {modeText[m].title}
              </strong>
              <span>{modeText[m].detail}</span>
            </button>
          ))}
        </div>
      </fieldset>
      {mode !== 'brand' && <LevelPicker side="brand" value={brandLevel} onChange={setBrandLevel} />}
      {mode !== 'rime' && <LevelPicker side="rime" value={rimeLevel} onChange={setRimeLevel} />}
      <p className="bot-start">
        <button
          type="button"
          className="btn btn-primary btn-large"
          onClick={() => onStart(players)}
        >
          {b.start}
        </button>
      </p>
    </article>
  );
}

function LevelPicker(props: {
  side: 'brand' | 'rime';
  value: IceBotLevel;
  onChange: (level: IceBotLevel) => void;
}) {
  const t = useT();
  return (
    <fieldset className="bot-picker">
      <legend>
        <SideDot side={props.side} />
        {t.botSetup.botOf(t.sides[props.side])}
      </legend>
      <div className="bot-strategies">
        {ICE_BOT_LEVELS.map((level) => (
          <button
            key={level.id}
            type="button"
            className="bot-strategy"
            aria-pressed={props.value === level.id}
            onClick={() => props.onChange(level.id)}
          >
            <strong>{t.bots[level.id].name}</strong>
            <span>{t.bots[level.id].detail}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}
